import { useEffect } from 'react';
import { useNostr } from '@nostrify/react';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAppContext } from '@/hooks/useAppContext';

/**
 * NostrSync - Syncs user's Nostr data
 *
 * This component runs globally to sync various Nostr data when the user logs in.
 * Currently syncs:
 * - NIP-65 relay list (kind 10002)
 * - Drafts (kind 30024)
 * - Published articles (kind 30023)
 */
export function NostrSync() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { config, updateConfig } = useAppContext();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user) return;

    const syncRelaysFromNostr = async () => {
      try {
        const events = await nostr.query(
          [{ kinds: [10002], authors: [user.pubkey], limit: 1 }],
          { signal: AbortSignal.timeout(5000) }
        );

        if (events.length > 0) {
          const event = events[0];

          // Only update if the event is newer than our stored data
          if (event.created_at > config.relayMetadata.updatedAt) {
            const fetchedRelays = event.tags
              .filter(([name]) => name === 'r')
              .map(([_, url, marker]) => ({
                url,
                read: !marker || marker === 'read',
                write: !marker || marker === 'write',
              }));

            if (fetchedRelays.length > 0) {
              console.log('Syncing relay list from Nostr:', fetchedRelays);
              updateConfig((current) => ({
                ...current,
                relayMetadata: {
                  relays: fetchedRelays,
                  updatedAt: event.created_at,
                },
              }));
            }
          }
        }
      } catch (error) {
        console.error('Failed to sync relays from Nostr:', error);
      }
    };

    syncRelaysFromNostr();
  }, [user, config.relayMetadata.updatedAt, nostr, updateConfig]);

  // Prefetch drafts when user logs in
  useEffect(() => {
    if (!user) return;

    const prefetchDrafts = async () => {
      try {
        await queryClient.prefetchQuery({
          queryKey: ['drafts', user.pubkey],
          queryFn: async () => {
            const events = await nostr.query(
              [{ kinds: [30024], authors: [user.pubkey] }],
              { signal: AbortSignal.timeout(5000) },
            );

            return events
              .filter(e => e.content.trim().length > 0)
              .map(event => {
                const getTag = (name: string) => event.tags.find(t => t[0] === name)?.[1] || '';
                const getTags = (name: string) => event.tags.filter(t => t[0] === name).map(t => t[1]);

                return {
                  id: event.id,
                  eventId: event.id,
                  title: getTag('title'),
                  summary: getTag('summary'),
                  content: event.content,
                  image: getTag('image'),
                  tags: getTags('t'),
                  slug: getTag('d'),
                  updatedAt: event.created_at * 1000,
                };
              })
              .sort((a, b) => b.updatedAt - a.updatedAt);
          },
          staleTime: 30 * 1000,
        });
      } catch (error) {
        console.error('Failed to prefetch drafts:', error);
      }
    };

    prefetchDrafts();
  }, [user, nostr, queryClient]);

  // Prefetch published articles when user logs in
  useEffect(() => {
    if (!user) return;

    const prefetchPublishedArticles = async () => {
      try {
        await queryClient.prefetchQuery({
          queryKey: ['published-articles', user.pubkey],
          queryFn: async () => {
            const events = await nostr.query(
              [{ kinds: [30023], authors: [user.pubkey] }],
              { signal: AbortSignal.timeout(5000) },
            );

            return events
              .filter(e => e.content.trim().length > 0)
              .map(event => {
                const getTag = (name: string) => event.tags.find(t => t[0] === name)?.[1] || '';
                const getTags = (name: string) => event.tags.filter(t => t[0] === name).map(t => t[1]);

                const publishedAtTag = getTag('published_at');
                const publishedAt = publishedAtTag ? parseInt(publishedAtTag) * 1000 : event.created_at * 1000;

                return {
                  id: event.id,
                  eventId: event.id,
                  title: getTag('title'),
                  summary: getTag('summary'),
                  content: event.content,
                  image: getTag('image'),
                  tags: getTags('t'),
                  slug: getTag('d'),
                  publishedAt,
                  updatedAt: event.created_at * 1000,
                };
              })
              .sort((a, b) => b.updatedAt - a.updatedAt);
          },
          staleTime: 30 * 1000,
        });
      } catch (error) {
        console.error('Failed to prefetch published articles:', error);
      }
    };

    prefetchPublishedArticles();
  }, [user, nostr, queryClient]);

  return null;
}